import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ManagementGrid } from './ManagementGrid'
import { ManagementGridExtendedColumn } from './types'
import { References } from './References'
import { DataGridRef, DataGridTypes } from 'devextreme-react/data-grid'
import { TokenCache } from '@azure/msal-browser/dist/cache/TokenCache'

/**
 * Simplified props for the "factory."
 * This will show a main grid for the "stateEntity" (like stato_analisi),
 * and a references column that fetches child items from "referencesCollection."
 */
interface ManagementGridFactoryProps {
  entity: string
  referencesCollection?: string
  referencesTitle?: string
  displayField?: string
  entityRoute?: string
  additionalColumns?: ManagementGridExtendedColumn[]
}

export function ManagementGridFactory({
  entity,
  referencesCollection,
  referencesTitle,
  displayField = 'ded_Dis',
  entityRoute,
  additionalColumns = [],
}: ManagementGridFactoryProps) {
  const dataGridRef = useRef<DataGridRef>(null)
  const [referenceCounts, setReferenceCounts] = useState<Map<any, number>>(new Map())
  const [rowToRepaint, setRowToRepaint] = useState<any | null>(null)

  const handleDataLoaded = useCallback((rowKey: any, count: number) => {
    setReferenceCounts((prevMap) => {
      if (prevMap.get(rowKey) === count) {
        return prevMap // No change, return the same map instance
      }
      // Count has changed, update the map and schedule a repaint for this row
      setRowToRepaint(rowKey)
      const newMap = new Map(prevMap)
      newMap.set(rowKey, count)
      return newMap
    })
  }, []) // Empty dependency array: handleDataLoaded is stable

  useEffect(() => {
    if (rowToRepaint !== null && dataGridRef.current) {
      dataGridRef.current.instance().repaintRows([rowToRepaint])
      setRowToRepaint(null) // Reset after repainting
    }
  }, [rowToRepaint, dataGridRef])

  const onCellPrepared = useCallback(
    // Caused by missing command property on the column object in the DataGridTypes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      console.log(e.column)
      if (e.rowType === 'data' && e.column.command === 'edit') {
        const deleteLink = e.cellElement.querySelector('.dx-link-delete') as HTMLElement | null
        if (!deleteLink) return

        const referenceCount = referenceCounts.get(e.key)

        if (referenceCount === undefined || referenceCount > 0) {
          deleteLink.style.display = 'none'
        } else {
          deleteLink.style.display = ''
        }
      }
    },
    [referenceCounts],
  )

  const columns: ManagementGridExtendedColumn[] = useMemo(
    () => [
      {
        dataField: 'id',
        caption: 'ID',
        dataType: 'number',
        allowEditing: false,
        width: 60,
      },
      {
        dataField: 'nome',
        caption: 'Nome',
        dataType: 'string',
        allowEditing: true,
      },
      {
        dataField: 'note',
        caption: 'Note',
        dataType: 'string',
        allowEditing: true,
      },
      ...additionalColumns,

      ...(referencesCollection && referencesTitle
        ? [
            {
              caption: referencesTitle,
              dataType: 'object' as DataGridTypes.DataType, // Keep as object for custom rendering
              allowEditing: false,
              allowSorting: false,
              cellRender: (cellTemplate: DataGridTypes.ColumnCellTemplateData) =>
                cellTemplate.data.id && (
                  <References
                    parentEntityPath={entity}
                    childCollectionName={referencesCollection}
                    childDisplayField={displayField}
                    childSelectFields={['id', displayField]}
                    entityRoute={entityRoute}
                    parentId={cellTemplate.data.id}
                    maxCount={4}
                    onDataLoaded={(count) => handleDataLoaded(cellTemplate.row.key, count)}
                  />
                ),
            },
          ]
        : []),
    ],
    [entity, referencesCollection, referencesTitle, displayField, entityRoute, additionalColumns, handleDataLoaded],
  )

  return (
    <div className="content-block dx-card wide-card">
      <ManagementGrid
        ref={dataGridRef}
        onCellPrepared={onCellPrepared} // Pass the onCellPrepared callback
        endpoint={`${import.meta.env.VITE_QSADMINAPI_HOST}/${entity}`}
        title={`${referencesTitle}`}
        columns={columns}
        expandRelations={[]}
        storageKey={`${entity}-state-management-grid`}
        defaultPageSize={20}
        allowAdding={true}
        allowUpdating={true}
        allowDeleting={true} // This enables the delete functionality overall
      />
    </div>
  )
}
